import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const ImpactAnalysis = ({ recommendations }) => {
  if (!recommendations || recommendations?.length === 0) {
    return null;
  }

  // Calculate aggregated metrics
  const totalRecommendations = recommendations?.length;
  const estimatedTimeline = totalRecommendations * 4; // 4 months per recommendation
  const estimatedInvestment = totalRecommendations * 50000;
  const projectedROI = estimatedInvestment * 3; // 300% ROI
  const resourcesRequired = totalRecommendations * 2.5; // Average 2.5 people per recommendation

  // Prepare chart data
  const chartData = recommendations?.map((rec, index) => ({
    name: `Action ${index + 1}`,
    investment: 50000,
    roi: 150000,
    timeline: 4
  }));

  return (
    <div className="bg-white rounded-xl shadow-elevation-1 p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
          <Icon name="TrendingUp" size={20} className="text-green-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-[#002B45]">Aggregated Impact Analysis</h3>
          <p className="text-sm text-gray-500">Combined projections for {totalRecommendations} selected recommendations</p>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Clock" size={16} className="text-blue-600" />
            <p className="text-xs font-semibold text-blue-900">Total Timeline</p>
          </div>
          <p className="text-3xl font-bold text-[#002B45]">{estimatedTimeline}</p>
          <p className="text-xs text-gray-600 mt-1">months</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="DollarSign" size={16} className="text-purple-600" />
            <p className="text-xs font-semibold text-purple-900">Total Investment</p>
          </div>
          <p className="text-3xl font-bold text-[#002B45]">${(estimatedInvestment / 1000)?.toFixed(0)}K</p>
          <p className="text-xs text-gray-600 mt-1">estimated cost</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-green-600" />
            <p className="text-xs font-semibold text-green-900">Projected ROI</p>
          </div>
          <p className="text-3xl font-bold text-[#1FB5A5]">${(projectedROI / 1000)?.toFixed(0)}K</p>
          <p className="text-xs text-gray-600 mt-1">300% return</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-4 border border-orange-200">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Users" size={16} className="text-orange-600" />
            <p className="text-xs font-semibold text-orange-900">Resources Needed</p>
          </div>
          <p className="text-3xl font-bold text-[#002B45]">{resourcesRequired?.toFixed(0)}</p>
          <p className="text-xs text-gray-600 mt-1">team members</p>
        </div>
      </div>

      {/* Investment vs ROI Chart */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-[#002B45] mb-4">Investment vs Expected ROI</h4>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload?.length) {
                  return (
                    <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                      <p className="font-semibold text-[#002B45] mb-2">{payload?.[0]?.payload?.name}</p>
                      <p className="text-sm text-blue-600">Investment: ${(payload?.[0]?.value / 1000)?.toFixed(0)}K</p>
                      <p className="text-sm text-green-600">Expected ROI: ${(payload?.[1]?.value / 1000)?.toFixed(0)}K</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Bar dataKey="investment" fill="#3B82F6" name="Investment" radius={[8, 8, 0, 0]} />
            <Bar dataKey="roi" fill="#1FB5A5" name="Expected ROI" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Key Insights */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-[#002B45] mb-4 flex items-center gap-2">
          <Icon name="Lightbulb" size={16} className="text-[#1FB5A5]" />
          Key Insights
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <Icon name="Target" size={20} className="text-blue-600 mb-2" />
            <p className="text-sm font-semibold text-blue-900 mb-1">Strategic Alignment</p>
            <p className="text-xs text-blue-700">
              Your selected recommendations align with {Math.round(totalRecommendations / 5 * 100)}% of your organization's data maturity improvement areas.
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <Icon name="Award" size={20} className="text-green-600 mb-2" />
            <p className="text-sm font-semibold text-green-900 mb-1">Quick Wins Identified</p>
            <p className="text-xs text-green-700">
              {recommendations?.filter(r => r?.priority === 1)?.length} high-priority actions can deliver measurable results within the first 6 months.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImpactAnalysis;